import Big from 'big.js';

import { getBarcodeError, getCAPError, getCPIError, getPriceError, getQuantityError, getSKUError } from '../validations/inventory';
import { getHeightError, getLengthError, getWeightError, getWidthError } from '../validations/shipping';
import { getCharactersCountError, getInstructionsError } from '../validations/personalization';
import { getDescriptionError } from '../validations/description';
import { getTitleError } from '../validations/title';
import { getSize } from '../../utils/iterable/getSize';

import { ETSY, SHOPIFY } from '../../constants/channels';
import { MENU_ITEM } from '../../constants/bulkEdit';
import {
  BARCODE,
  CAP,
  CHAR_COUNT_MAX,
  COUNTRY_CODE,
  CPI,
  DESCRIPTION,
  DIGITAL,
  HEIGHT,
  HS_CODE,
  INSTRUCTIONS,
  LENGTH,
  PHYSICAL,
  PRICE,
  PROFILE_ID,
  QUANTITY,
  RETURN_POLICY,
  SKU,
  TITLE,
  TRACK_QUANTITY,
  UNIT,
  WEIGHT,
  WIDTH,
} from '../../constants/attributes';

export function getMenuItemByAttribute({ attribute, channel }) {
  switch (attribute) {
    case BARCODE: {
      return MENU_ITEM.BARCODE;
    }

    case CAP: {
      return MENU_ITEM.CAP;
    }

    case COUNTRY_CODE: {
      return MENU_ITEM.COUNTRY_CODE;
    }

    case CPI: {
      return MENU_ITEM.CPI;
    }

    case HEIGHT:
    case LENGTH:
    case WIDTH: {
      return MENU_ITEM.ITEM_SIZE;
    }

    case HS_CODE: {
      return MENU_ITEM.HS_CODE;
    }

    case PRICE: {
      return MENU_ITEM.PRICE;
    }

    case PROFILE_ID: {
      return MENU_ITEM.SHIPPING_PROFILE;
    }

    case QUANTITY:
    case TRACK_QUANTITY: {
      return MENU_ITEM.QUANTITY;
    }

    case SKU: {
      return MENU_ITEM.SKU;
    }

    case UNIT: {
      return MENU_ITEM.WEIGHT;
    }

    case WEIGHT: {
      switch (channel) {
        case ETSY: {
          return MENU_ITEM.ITEM_WEIGHT;
        }

        case SHOPIFY: {
          return MENU_ITEM.WEIGHT;
        }

        default: {
          return undefined;
        }
      }
    }

    case RETURN_POLICY: {
      return MENU_ITEM.RETURN_POLICY;
    }

    default: {
      return undefined;
    }
  }
}

export function getError({ type, value, ...rest }) {
  switch (type) {
    case BARCODE: {
      return getBarcodeError({ barcode: value, ...rest });
    }

    case CAP: {
      return getCAPError({ cap: value, ...rest });
    }

    case CHAR_COUNT_MAX: {
      return getCharactersCountError({ value, ...rest });
    }

    case CPI: {
      return getCPIError({ cpi: value, ...rest });
    }

    case DESCRIPTION: {
      return getDescriptionError({ value, ...rest });
    }

    case HEIGHT: {
      return getHeightError({ height: value, ...rest });
    }

    case INSTRUCTIONS: {
      return getInstructionsError({ value, ...rest });
    }

    case LENGTH: {
      return getLengthError({ length: value, ...rest });
    }

    case PRICE: {
      return getPriceError({ price: value, ...rest });
    }

    case QUANTITY: {
      return getQuantityError({ quantity: value, ...rest });
    }

    case SKU: {
      return getSKUError({ sku: value, ...rest });
    }

    case TITLE: {
      return getTitleError({ value, ...rest });
    }

    case WEIGHT: {
      return getWeightError({ weight: value, ...rest });
    }

    case WIDTH: {
      return getWidthError({ width: value, ...rest });
    }

    default: {
      return false;
    }
  }
}

export function getNumberValue({ decimals, operationType, operationValue, productValue }) {
  const type = (/[^\.]+$/.exec(operationType) || [])[0];
  const number = new Big(operationValue || 0);
  let result = new Big(productValue || 0);

  switch (type) {
    case 'set':
    case 'changeTo': {
      if (number) {
        result = number;
      }

      break;
    }

    case 'increaseBy': {
      result = result.plus(number);
      break;
    }

    case 'decreaseBy': {
      result = result.minus(number);
      break;
    }

    default:
      break;
  }

  return Number.isFinite(decimals)
    ? result.toFixed(decimals)
    : result.toString();
}

export function getPriceValue({ absolute, cents, operationType, operationValue, productValue }) {
  const type = (/[^\.]+$/.exec(operationType) || [])[0];
  const number = new Big(operationValue || 0);
  const hundred = Big(100);
  let result = new Big(productValue || 0);

  switch (type) {
    case 'set':
    case 'changeTo': {
      if (number) {
        result = number;
      }

      break;
    }

    case 'increaseBy': {
      if (number.gt(0)) {
        if (absolute) {
          result = result.plus(number);
        } else {
          result = result.plus(result.times(number.div(hundred)));
        }
      }

      break;
    }

    case 'decreaseBy': {
      if (number.gt(0)) {
        if (absolute) {
          result = result.minus(number);
        } else {
          result = result.minus(result.times(number.div(hundred)));
        }
      }

      break;
    }

    default:
      break;
  }

  if (cents) {
    result = result.round(0, 0)[result.s > 0 ? 'plus' : 'minus'](new Big(cents).div(hundred));
  }

  return result.toFixed(2);
}

export function getFiltersByMenuItemId({ filters, menuItemId, type, optionals, categories = [] }) {
  // TODO: refactor "Optional" to get rid of this
  function optionalReduce(acc, item) {
    const title = item.get('id') || '';
    return title ? acc.concat(title) : acc;
  }
  const optionalItems = getSize(optionals) ? optionals.reduce(optionalReduce, []) : [];

  switch (menuItemId) {
    case MENU_ITEM.FILES: {
      return type === DIGITAL || filters.get('is_digital') === true
        ? undefined
        : filters
          .set('is_digital', true)
          .set('offset', 0);
    }

    case MENU_ITEM.ITEM_SIZE:
    case MENU_ITEM.ITEM_WEIGHT:
    case MENU_ITEM.RETURN_POLICY:
    case MENU_ITEM.SHIPPING_PROFILE:
    case MENU_ITEM.VARIATIONS: {
      return type === PHYSICAL || filters.get('is_digital') === false
        ? undefined
        : filters
          .set('is_digital', false)
          .set('offset', 0);
    }

    default: {
      if (optionalItems?.includes(menuItemId) && getSize(categories)) {
        return filters
          .set('taxonomy_id', categories)
          .set('offset', 0);
      }

      const emptyFilters = filters.deleteAll(['limit', 'offset', 'products']);
      return getSize(emptyFilters)
        ? filters.deleteAll(emptyFilters.keySeq().toList()).set('offset', 0)
        : undefined;
    }
  }
}

export function shapeFiltersForAPI({ filters, filterByErrors, invalidProducts }) {
  function reduceProductIds(result, productId) {
    if (invalidProducts.has(productId)) {
      result.push(productId);
    }

    return result;
  }

  function reduceFilters(result, value, key) {
    switch (key) {
      case 'products': {
        result.id = filterByErrors
          ? value.reduce(reduceProductIds, [])
          : value.toArray();

        break;
      }

      default: {
        result[key] = value;
        break;
      }
    }

    return result;
  }

  return filters.reduce(reduceFilters, {});
}
